import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Container, Link, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import ApplciationModal from '../navigation/modals/applicationModal';
import { toast } from 'react-toastify';
import { sortApplicationItems } from '../common/utils/tableUtil';
import PageHeader from '../common/banner/pageHeader';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import SearchBar from '../common/search/SearchBar';
import { useNavigate } from 'react-router-dom';
import AlertsFilterButton from '../common/buttons/AlertsFilterButton';

const getFilteredApplications = (applications, filterValue) => {
  if (filterValue === 'all') return applications;

  return applications.filter(application => {
    if (filterValue === 'authorized') {
      return application.authorized === true;
    } else if (filterValue === 'rogue') {
      return application.authorized === false;
    }
    return true;
  });
}

const ApplicationTable = ({ items, onToggle, onIpClick, sortConfig, onSortChange, fetchApplications }) => {

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  }

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  }

  const applicationUpdate = (application) => {
      toast.success(`Application ${application.id} Updated`);
      fetchApplications();
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['IP (DNS)', 'Collector', 'App Name', 'Port', 'Authorized', 'Notes'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}              
                <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>
                    {item.ip_dns !== 'undefined' ? (
                        <Link underline="hover" color="#c1d4e7" 
                            sx={{ '&:hover': { cursor: 'pointer' } }}
                            onClick={ (e) => { onIpClick(e.target.text )}}
                        >
                            {item.ip_dns}
                        </Link>
                      ) : (
                        <span>undefined</span>
                      )}
                </TableCell>                
                <TableCell sx={{ color: '#FFFFFF' }}>{item.collector}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.name}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.port}</TableCell>
                <TableCell>
                  <Switch checked={!!item.authorized} onChange={(e) => onToggle(item, 'authorized', e.target.checked)} />
                </TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.notes}</TableCell>
                <TableCell>
                    <ApplciationModal application={item} onApplicationUpdate={applicationUpdate} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default function ApplicationSettings() {
  const [applications, setApplications] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [applicationsFilter, setApplicationsFilter] = useState('all');
  const [ipv4, setIPv4] = useState("");

  const title = 'Application Authorization';
  const { paramFilter } = useParams();
  const navigate = useNavigate();

  useEffect( () => {
    if (ipv4 != "") {
      navigate('/hostInformation', {state: {ipv4}})
    }
    fetchApplications();
    updateApplicationsFilter();
  }, [ipv4]);

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/applications/`);
      if (!response.ok) {
          toast.error(`HTTP error! status: ${response.status}`);
      } else {
          const json = await response.json();
          setApplications(json.data);          
      }
    } catch (e) {
      console.error(e);
    }
  }

  const handleIpClick = (ip) => {
      setIPv4(ip);
  };

  const updateApplicationsFilter = () => {
    if (paramFilter !== undefined) {
      setApplicationsFilter(paramFilter);
    }
  }

  const handleUpdate = async (item) => {
    const res = await fetch(`${process.env.REACT_APP_API}/applications/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
    });
    if (res.ok) {
        setApplications((prev) => prev.map((p) => (p.id === item.id ? item : p)));
        if (item.authorized) {
            toast.success(`Application ${item.id} Authorized`);
        } else {
          toast.success(`Application ${item.id} De-Authorized`);
        }
    }
  };

  const handleSortChange = (newConfig) => {
    setSortConfig(newConfig);
  };

  const handleFilterChange = (value) => {
    setApplicationsFilter(value);
    if (value === 'all') {
      navigate(`/applications`);
    } else {
      navigate(`/applications/${value}`);
    }
  };

  // apply alerts filter
  const applicationsFiltered = getFilteredApplications(applications, paramFilter);

  const filtered = applicationsFiltered.filter((item) => {
    const s = searchQuery.toLowerCase();
    return ['collector', 'name', 'notes'].some((field) => item[field]?.toLowerCase().includes(s));
  });

  const sorted = sortApplicationItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleBreadcrumbClick = () => {
    navigate('/alerts');
  }

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 5 }}>
          <Box sx={{pt: 2}}>
            <ClassificationBannerHeader></ClassificationBannerHeader>
            <PageHeader title="Applications"></PageHeader>
          </Box>

          <Box>
              <Box sx={{ display: 'flex', mt: 2, justifyContent: 'center', gap: '32px', width: '100%', maxwidth: '1600px', margin: '30px auto 0' }}>
                  <Stack  sx={{ width: '47%', paddingLeft: 0, marginLeft: 0}} spacing={{ md: 4 }}  >
                      <Breadcrumbs aria-label="breadcrumb">
                          <Link underline="hover" color="#c1d4e7" onClick={handleBreadcrumbClick} sx={{ '&:hover': { cursor: 'pointer' } }}>
                              Alerts
                          </Link>
                          <Typography color="white"> / </Typography>
                          <Typography color="white">{ title }</Typography>
                      </Breadcrumbs>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" sx={{ width: '50%', mx: 'auto' }}>          
                    <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search applications..."
                      label="Filter"
                      width={200}
                    />
                    <AlertsFilterButton
                      value={applicationsFilter}
                      onChange={(value) => {
                        handleFilterChange(value);
                        setPage(0);
                      }}
                    />
                  </Stack>
                </Box>

              <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                  <ApplicationTable
                      items={paginated}
                      onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
                      onIpClick={(ip) => handleIpClick(ip) }
                      sortConfig={sortConfig}
                      onSortChange={handleSortChange}
                      setApplications={setApplications}
                      fetchApplications={fetchApplications}
                  />
                  <TablePagination
                      component="div"
                      count={sorted.length}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                  />
              </TableContainer>
         </Box>
    </Container>
  );
}